<template>
  <BaseDrawer
    shrink
    :is-open="isRevealed"
    @close="handleClose"
  >
    <div class="h-full overflow-y-auto">
      <template v-if="step === 'initial'">
        <DrawerHeader @close="handleClose">
          Receipt
        </DrawerHeader>
        <div class="px-4 pb-4">
          <ActionItemSmall
            v-if="printingSupported"
            label="Print Merchant Receipt"
            :icon="IconPrintReceipt"
            @click="handlePrintMerchantReceipt"
          />
          <ActionItemSmall
            label="Email Receipt"
            :icon="IconEmailReceipt"
            @click="handleGotoEmail"
          />

          <ActionItemSmall
            v-if="organizationConfig?.smsServiceEnabled"
            label="SMS Receipt"
            :icon="IconSMSReceipt"
            @click="handleGotoSMS"
          />
          <ActionItemSmall
            v-if="printingSupported"
            label="Print Receipt"
            :icon="IconPrintReceipt"
            @click="handlePrintReceipt"
          />
        </div>
      </template>
      <template v-else-if="step === 'email'">
        <DrawerHeader @close="handleBack">
          Email Receipt
        </DrawerHeader>
        <ReceiptEmailForm
          :object-id="orderId"
          receipt-type="order"
          class="px-4 pb-4"
          @submit-success="handleClose"
        />
      </template>
      <template v-else-if="step === 'sms'">
        <DrawerHeader @close="handleBack">
          SMS Receipt
        </DrawerHeader>
        <ReceiptSMSForm
          :object-id="orderId"
          receipt-type="order"
          class="px-4 pb-4"
          @submit-success="handleClose"
        />
      </template>
    </div>
  </BaseDrawer>
</template>

<script setup lang="ts">
import {
  IconEmailReceipt,
  IconSMSReceipt,
  IconPrintReceipt,
} from "@/icons"

import { retrieveOrderReceipt } from "@/services/orders/retrieveOrderReceipt"
import { useConfirmDialog } from "@vueuse/core"
import { IPrinter } from "@/androidPlugins/IPrinter"
import { useOrganizationConfig } from "@/services/organizations/retrieveOrganizationConfig"

const props = withDefaults(
  defineProps<{
    orderId: string
  }>(),
  {}
)
const emit = defineEmits(["close"])

const { reveal, isRevealed, cancel, onReveal } =
  useConfirmDialog()

const { organizationConfig } = useOrganizationConfig()

defineExpose({ reveal })

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { printingSupported } = useDevice()

const step = ref<"initial" | "email" | "sms">("initial")
const handleGotoEmail = () => {
  step.value = "email"
}
const handleGotoSMS = () => {
  step.value = "sms"
}

const handleBack = () => {
  step.value = "initial"
}

const handleClose = () => {
  cancel()
}

onReveal(() => {
  step.value = "initial"
})

const handlePrintReceipt = async () => {
  startLoading()
  try {
    const { receipt, webReceiptUrl } =
      await retrieveOrderReceipt(props.orderId)
    await IPrinter.printStr({
      str: receipt,
      footerQrCodeContent: webReceiptUrl,
    })
    handleClose()
  } catch {
    toast.error("Failed to generate receipt")
  } finally {
    stopLoading()
  }
}

const handlePrintMerchantReceipt = async () => {
  startLoading()
  try {
    const { receipt } = await retrieveOrderReceipt(
      props.orderId,
      { isMerchant: true }
    )
    await IPrinter.printStr({
      str: receipt,
    })

    handleClose()
  } catch {
    toast.error("Failed to generate receipt")
  } finally {
    stopLoading()
  }
}
</script>
